import React, {
  Children,
  forwardRef,
  isValidElement,
  OptionHTMLAttributes,
  ReactElement,
  SelectHTMLAttributes,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  FieldContainer,
  FieldLabel,
  FieldMessage,
} from '@components/formField/formField.styled'

import {
  NativeSelect,
  SelectCheck,
  SelectChevron,
  SelectFieldWrapper,
  SelectList,
  SelectOption,
  SelectTrigger,
} from './selectField.styled'

type SelectOptionData = {
  disabled: boolean
  label: string
  value: string
}

export type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string
  error?: string
  hint?: string
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      children,
      className,
      defaultValue,
      disabled,
      error,
      hint,
      id: providedId,
      label,
      onChange,
      required,
      value,
      ...selectProps
    },
    forwardedRef
  ) => {
    const generatedId = useId()
    const id = providedId ?? generatedId
    const listId = `${id}-listbox`
    const message = error ?? hint
    const messageId = message ? `${id}-message` : undefined
    const wrapperRef = useRef<HTMLDivElement>(null)
    const nativeSelectRef = useRef<HTMLSelectElement | null>(null)
    const [open, setOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const [internalValue, setInternalValue] = useState(
      defaultValue === undefined ? '' : String(defaultValue)
    )
    const controlled = value !== undefined
    const selectedValue = controlled ? String(value) : internalValue

    const options = useMemo(
      () =>
        Children.toArray(children).flatMap((child): SelectOptionData[] => {
          if (!isValidElement(child) || child.type !== 'option') return []
          const option = child as ReactElement<
            OptionHTMLAttributes<HTMLOptionElement>
          >
          return [
            {
              disabled: Boolean(option.props.disabled),
              label: String(option.props.children ?? ''),
              value: String(option.props.value ?? option.props.children ?? ''),
            },
          ]
        }),
      [children]
    )

    const selectedIndex = Math.max(
      0,
      options.findIndex((option) => option.value === selectedValue)
    )
    const selectedOption = options[selectedIndex]

    const assignNativeRef = (element: HTMLSelectElement | null) => {
      nativeSelectRef.current = element
      if (typeof forwardedRef === 'function') {
        forwardedRef(element)
      } else if (forwardedRef) {
        forwardedRef.current = element
      }
    }

    useEffect(() => {
      const closeOnOutsideClick = (event: MouseEvent) => {
        if (!wrapperRef.current?.contains(event.target as Node)) {
          setOpen(false)
        }
      }
      document.addEventListener('mousedown', closeOnOutsideClick)
      return () =>
        document.removeEventListener('mousedown', closeOnOutsideClick)
    }, [])

    const selectValue = (nextValue: string) => {
      const nativeSelect = nativeSelectRef.current
      if (!nativeSelect) return
      if (!controlled) setInternalValue(nextValue)
      const valueSetter = Object.getOwnPropertyDescriptor(
        HTMLSelectElement.prototype,
        'value'
      )?.set
      valueSetter?.call(nativeSelect, nextValue)
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }))
      setOpen(false)
    }

    const moveActiveOption = (direction: 1 | -1) => {
      if (!options.length) return
      let nextIndex = activeIndex
      do {
        nextIndex = (nextIndex + direction + options.length) % options.length
      } while (options[nextIndex].disabled && nextIndex !== activeIndex)
      setActiveIndex(nextIndex)
    }

    return (
      <FieldContainer className={className}>
        <FieldLabel id={`${id}-label`}>
          {label}
          {required ? ' *' : ''}
        </FieldLabel>
        <SelectFieldWrapper ref={wrapperRef}>
          <NativeSelect
            {...selectProps}
            ref={assignNativeRef}
            id={`${id}-native`}
            value={selectedValue}
            disabled={disabled}
            required={required}
            tabIndex={-1}
            aria-hidden="true"
            onChange={(event) => {
              if (!controlled) setInternalValue(event.target.value)
              onChange?.(event)
            }}
          >
            {children}
          </NativeSelect>
          <SelectTrigger
            id={id}
            type="button"
            role="combobox"
            disabled={disabled}
            $invalid={Boolean(error)}
            aria-controls={listId}
            aria-describedby={messageId}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-invalid={Boolean(error)}
            aria-labelledby={`${id}-label`}
            aria-activedescendant={
              open ? `${id}-option-${activeIndex}` : undefined
            }
            onClick={() => {
              setActiveIndex(selectedIndex)
              setOpen((current) => !current)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setOpen(false)
                return
              }
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault()
                if (!open) {
                  setActiveIndex(selectedIndex)
                  setOpen(true)
                } else {
                  moveActiveOption(event.key === 'ArrowDown' ? 1 : -1)
                }
                return
              }
              if (
                open &&
                (event.key === 'Enter' || event.key === ' ') &&
                !options[activeIndex]?.disabled
              ) {
                event.preventDefault()
                selectValue(options[activeIndex].value)
              }
            }}
          >
            <span>{selectedOption?.label ?? 'Vyberte možnost'}</span>
            <SelectChevron $open={open} aria-hidden="true" />
          </SelectTrigger>
          {open && (
            <SelectList
              id={listId}
              role="listbox"
              aria-labelledby={`${id}-label`}
            >
              {options.map((option, index) => (
                <SelectOption
                  id={`${id}-option-${index}`}
                  key={option.value}
                  role="option"
                  $active={activeIndex === index}
                  $selected={option.value === selectedValue}
                  aria-disabled={option.disabled}
                  aria-selected={option.value === selectedValue}
                  onMouseEnter={() => {
                    if (!option.disabled) setActiveIndex(index)
                  }}
                  onClick={() => {
                    if (!option.disabled) selectValue(option.value)
                  }}
                >
                  <span>{option.label}</span>
                  {option.value === selectedValue && (
                    <SelectCheck aria-hidden="true" />
                  )}
                </SelectOption>
              ))}
            </SelectList>
          )}
        </SelectFieldWrapper>
        {message && (
          <FieldMessage
            id={messageId}
            $invalid={Boolean(error)}
            role={error ? 'alert' : undefined}
          >
            {message}
          </FieldMessage>
        )}
      </FieldContainer>
    )
  }
)

SelectField.displayName = 'SelectField'
